import { Type } from '@sinclair/typebox';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
/**
 * Shared contracts for long-running track jobs (COP-156; TFDX-13b, TFDX-13d).
 *
 * A track tool call can answer `202` with a `LongRunningEnvelope` and finish
 * its work later. The hub stores one `tf_track_job` row per accepted call; the
 * job id IS the tool call id of the call that opened it.
 */
/** Request header the hub sends on a track tool call; the value is the tool call id. */
export const TRACK_JOB_ID_HEADER = 'X-Trackframe-Job-Id';
/** Longest `progress` text a track may report. */
export const TRACK_JOB_PROGRESS_MAX_CHARS = 500;
/** Longest failure reason a track may report. */
export const TRACK_JOB_FAILURE_MAX_CHARS = 2000;
/** Largest `done` result, as `JSON.stringify(data)` UTF-8 bytes (256 KiB). */
export const TRACK_JOB_RESULT_MAX_BYTES = 262144;
/** The statuses a TRACK may report (TFDX-13b). */
export const TrackJobStatusSchema = Type.Union([
    Type.Literal('started'),
    Type.Literal('progress'),
    Type.Literal('done'),
    Type.Literal('failed'),
]);
/**
 * The statuses the HUB stores and shows: the four a track reports plus
 * `unknown`, which only the hub sets when a job never reported a result.
 * Wire mirror of the `tf_track_job_status` pg enum.
 */
export const TrackJobStateSchema = Type.Union([
    Type.Literal('started'),
    Type.Literal('progress'),
    Type.Literal('done'),
    Type.Literal('failed'),
    Type.Literal('unknown'),
]);
/**
 * One envelope, both directions (TFDX-13b): the `202` body a track returns to
 * open a job, and the body it posts to report progress or the end of the job.
 * No `additionalProperties: false` — a track may add keys of its own.
 */
export const LongRunningEnvelopeSchema = Type.Object({
    type: Type.Literal('longRunning'),
    // A tool call id, never a UUID — AI SDK tool call ids are opaque strings.
    jobId: Type.String({ minLength: 1, maxLength: 200 }),
    status: TrackJobStatusSchema,
    data: Type.Optional(Type.Unknown()),
});
export const TrackJobParamsSchema = Type.Object({
    toolCallId: Type.String({ minLength: 1, maxLength: 200 }),
});
export const TrackJobStatusResponseSchema = Type.Object({
    status: TrackJobStateSchema,
    finishedAt: Type.Optional(IsoDateTimeSchema),
});
/**
 * The job row as its owner reads it. It deliberately has NO `result` and NO
 * `input` field: the result reaches the model through the continuation turn
 * only, and the input is an argument bag (HUB-024).
 */
export const TrackJobDtoSchema = Type.Object({
    jobId: Type.String(),
    toolName: Type.String(),
    trackId: UuidSchema,
    sessionId: UuidSchema,
    status: TrackJobStateSchema,
    progress: Type.Union([Type.String(), Type.Null()]),
    error: Type.Union([Type.String(), Type.Null()]),
    startedAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
    finishedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    narrated: Type.Boolean(),
    /**
     * COP-157 D5 (AGENT-08e) — whether the narration exists: `none` = not
     * claimed; `claimed` = a narration turn claimed the job but its anchor row
     * holds no parts yet; `persisted` = the anchor row holds the narration.
     */
    narration: Type.Union([Type.Literal('none'), Type.Literal('claimed'), Type.Literal('persisted')]),
    /**
     * COP-157 D5 / COP-160 P29 (AGENT-08d) — who continues the finished job:
     * `chat` = a chat narration turn (the `narration` above moves); `execution`
     * = the execution's resume (no chat narration ever comes, `narration` stays
     * `none`); `none` = not marked yet.
     */
    continuation: Type.Union([Type.Literal('chat'), Type.Literal('none'), Type.Literal('execution')]),
});
/**
 * Payload of the `data-job-pending` part (COP-156 D6). Like its sibling part
 * schemas (`TurnActionsPartDataSchema`) it does NOT set
 * `additionalProperties: false`, so an older consumer keeps working when the
 * shape grows.
 */
export const JobPendingPartDataSchema = Type.Object({
    jobId: Type.String(),
    toolCallId: Type.String(),
    toolName: Type.String(),
    trackId: UuidSchema,
    startedAt: IsoDateTimeSchema,
    status: TrackJobStateSchema,
    progress: Type.Optional(Type.String({ maxLength: TRACK_JOB_PROGRESS_MAX_CHARS })),
    narrated: Type.Optional(Type.Boolean()),
});
//# sourceMappingURL=track-jobs.js.map